import { useState } from "react";
import API from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { PasswordFields, passwordProblem } from "./PasswordFields";
import { UserPlus, EnvelopeSimple, Key, Check, Copy, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const absolute = (r) => (r?.invite_path ? `${window.location.origin}${r.invite_path}` : r?.invite_url);

export function PlatformAdminAddDialog({ open, onClose, onAdded }) {
  const [form, setForm] = useState({ name: "", email: "" });
  const [mode, setMode] = useState("invite");
  const [password, setPassword] = useState("");
  const [forceChange, setForceChange] = useState(true);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setForm({ name: "", email: "" }); setMode("invite"); setPassword(""); setForceChange(true); setResult(null); };
  const close = () => { reset(); onClose(); };
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { toast.error("Name and e-mail are required"); return; }
    if (mode === "password" && passwordProblem(password)) { toast.error(passwordProblem(password)); return; }
    setSaving(true);
    try {
      const { data } = await API.post("/platform/admins/invite", { ...form, mode, password: mode === "password" ? password : undefined, force_change: forceChange });
      setResult(data);
      toast.success(data.message);
      onAdded?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Couldn't add the admin"); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="sm:max-w-[540px]" data-testid="platform-admin-add-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><UserPlus size={20} /> Add platform admin</DialogTitle>
          <DialogDescription>Platform admins can see every workspace and all console settings.</DialogDescription>
        </DialogHeader>
        {result ? (
          <div className="space-y-3" data-testid="platform-admin-add-result">
            <div className="rounded-lg border border-primary/20 bg-primary/10 p-4 text-sm space-y-2">
              <p className="font-medium flex items-center gap-2"><Check size={16} weight="bold" /> {result.message}</p>
              <p className="text-muted-foreground text-xs">{result.email}</p>
              {result.mode === "invite" && (
                <>
                  <p className={`text-xs flex items-center gap-1 ${result.email_sent ? "text-emerald-700 dark:text-emerald-400" : "text-amber-700 dark:text-amber-400"}`} data-testid="platform-admin-add-email-status">
                    {result.email_sent ? <><Check size={12} /> Invitation e-mail sent</> : <><WarningCircle size={12} /> E-mail not sent{result.email_error ? ` — ${result.email_error}` : ""}. Send them the link below.</>}
                  </p>
                  <Label className="text-xs">Invitation link (valid 14 days)</Label>
                  <div className="flex gap-2">
                    <Input value={absolute(result)} readOnly className="font-mono text-xs" data-testid="platform-admin-add-link" />
                    <Button type="button" variant="outline" size="sm" onClick={() => copy(absolute(result))} data-testid="platform-admin-add-copy"><Copy size={14} /></Button>
                  </div>
                </>
              )}
              {result.mode === "password" && (
                <div className="space-y-1">
                  <Label className="text-xs">Password you set (share it directly — it is not e-mailed)</Label>
                  <div className="flex gap-2">
                    <Input value={password} readOnly className="font-mono text-xs" data-testid="platform-admin-add-password" />
                    <Button type="button" variant="outline" size="sm" onClick={() => copy(password)} data-testid="platform-admin-add-copy-password"><Copy size={14} /></Button>
                  </div>
                  {result.force_password_change && <p className="text-xs text-muted-foreground">They'll be asked to choose their own password at first sign-in.</p>}
                </div>
              )}
            </div>
            <DialogFooter><Button onClick={close} data-testid="platform-admin-add-done">Done</Button></DialogFooter>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="pa-name">Full name</Label>
              <Input id="pa-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Sam Taylor" required data-testid="platform-admin-add-name" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pa-email">E-mail</Label>
              <Input id="pa-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="sam@yourcompany.co.uk" required data-testid="platform-admin-add-email" />
            </div>
            <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="How they get access">
              {[["invite", EnvelopeSimple, "E-mail them a link", "They choose their own password"], ["password", Key, "Set a password now", "You hand the password over"]].map(([k, Icon, label, sub]) => (
                <button key={k} type="button" role="radio" aria-checked={mode === k} onClick={() => setMode(k)} data-testid={`platform-admin-mode-${k}`}
                  className={`text-left rounded-lg border px-3 py-2 transition-colors ${mode === k ? "border-primary bg-primary/5" : "border-border hover:bg-muted/40"}`}>
                  <span className="flex items-center gap-1.5 text-sm font-medium"><Icon size={14} /> {label}</span>
                  <span className="block text-[11px] text-muted-foreground">{sub}</span>
                </button>
              ))}
            </div>
            {mode === "password" && <PasswordFields value={password} onChange={setPassword} forceChange={forceChange} onForceChange={setForceChange} idPrefix="platform-admin-pw" />}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={close} data-testid="platform-admin-add-cancel">Cancel</Button>
              <Button type="submit" disabled={saving || (mode === "password" && (!password || !!passwordProblem(password)))} data-testid="platform-admin-add-submit">
                {saving ? "Adding…" : mode === "invite" ? "Send invitation" : "Create admin"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
